import React from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Add } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppDraggableList from "components/Shared/AppDraggableList";
import ListOptionItem from "components/ListOption/ListOptionItem";
import ListOptionForm from "components/ListOption/ListOptionForm";
import FormDrawer from "components/Shared/FormDrawer";
import RowsSkeleton from "components/Shared/Skeleton/RowsSkeleton";
import PopulatePerformancePoints from "components/ListOption/PopulatePerformancePoints";

import { useAppMeta, useReOrderDraggableList } from "hooks";

import {
  useAddAccountListOptionMutation,
  useLazyGetAccountListDetailQuery,
  usePopulateAccountListMutation,
  useReOrderAccountListOptionsMutation,
  useUpdateAccountListOptionMutation,
} from "store/services/accountListApi";

import { ACCOUNT_LIST_OPTIONS } from "data/static";

const AccountListOptionDetailScreen = () => {
  const [showForm, setShowForm] = React.useState(false);
  const [selectedOption, setSelectedOption] = React.useState(null);

  const navigate = useNavigate();

  const { type: listId } = useParams();

  const [searchParams] = useSearchParams();

  const _type = searchParams.get("type");

  const listTypeParams = React.useMemo(() => {
    return _type?.split(",");
  }, [_type]);

  const _currentListType = React.useMemo(() => {
    return listTypeParams[listTypeParams?.length - 1];
  }, [listTypeParams]);

  const _currentListStaticDetail = ACCOUNT_LIST_OPTIONS.find(
    (lo) => lo.name === _currentListType
  );

  const optionBreadcrumbs = React.useMemo(
    () =>
      listTypeParams?.reduce((acc, param, index) => {
        if (listTypeParams.length === index) {
          acc.push({ title: param });
          return acc;
        }

        const _typeParams = listTypeParams
          ?.slice(index, listTypeParams?.length - 1)
          .join(",");

        const path = `/app/crm/account-list/detail/${listTypeParams[0]}?type=${_typeParams}`;

        acc.push({
          title: param,
          path,
        });

        return acc;
      }, []),
    [listTypeParams]
  );

  const breadcrumbs = [
    { title: "Account List", path: "/app/crm/account-list" },
    ...optionBreadcrumbs,
  ];

  // RTK Query
  const [
    fetchAccountList,
    {
      isLoading: loadingList,
      isFetching: fetchingList,
      data: accountListDetail,
      error,
    },
  ] = useLazyGetAccountListDetailQuery();

  const [addAccountListOption, { isLoading: addingOption }] =
    useAddAccountListOptionMutation();

  const [updateAccountListOption, { isLoading: updatingOption }] =
    useUpdateAccountListOptionMutation();

  const [reorderListItems, { isLoading: reOrderingList }] =
    useReOrderAccountListOptionsMutation();

  const [populateAccountList, { isLoading }] = usePopulateAccountListMutation();

  const serialNumberInitials = React.useMemo(() => {
    if (!_currentListStaticDetail?.name) return;
    return _currentListStaticDetail?.name
      ?.split(" ")
      ?.map((el) => el.charAt(0)?.toUpperCase())
      ?.join("");
  }, [_currentListStaticDetail?.name]);

  const { orderedList, handleOnDragEnd } = useReOrderDraggableList({
    listItems: accountListDetail?.children ?? [],
    serialNumberInitials,
  });

  const _showPopulateDefaults =
    !orderedList?.length && _currentListStaticDetail?.defaults?.length;

  // methods
  const handlePopulateAccountOptions = async (options) => {
    try {
      await populateAccountList({
        type: _currentListStaticDetail?.value,
        options: options,
      }).unwrap();
      toast.success(`${_currentListStaticDetail?.value} options populated`);
    } catch (err) {
      toast.error(
        err?.data?.message ??
          err?.message ??
          `Couldn't populate ${_currentListStaticDetail?.value}`
      );
    }
  };

  const handleClose = () => {
    setShowForm(false);
    setSelectedOption(null);
  };

  const handleAddOption = async (data) => {
    if (!data) return;

    if (ACCOUNT_LIST_OPTIONS?.find((o) => o.value === data?.name))
      return toast.error(
        "Option name cannot be one of the predefined list name"
      );

    const payload = {
      ...data,
      type: _currentListType,
      isActive: data?.status === "Active",
      level: accountListDetail.level ? accountListDetail.level + 1 : 1,
      maxLevel: _currentListStaticDetail?.maxLevel || 2,
      ...(accountListDetail?._id && {
        parent: accountListDetail?._id,
      }),
      parentName: accountListDetail?.name ?? _currentListStaticDetail?.value,
    };

    try {
      await addAccountListOption(payload).unwrap();
      toast.success("Account List Option added successfully!");
      handleClose();
    } catch (err) {
      const errorMessage =
        err?.data?.message || "Couldn't add Account List Option!";
      toast.error(errorMessage);
    }
  };

  const handleUpdateOption = async (data) => {
    if (!selectedOption) return;

    const payload = {
      ...selectedOption,
      ...data,
      type: _currentListType,
      isActive: data?.status === "Active",
    };

    try {
      await updateAccountListOption(payload).unwrap();
      toast.success("Account List Option successfully updated!");
      handleClose();
    } catch (err) {
      const errorMessage =
        err?.data?.message || "Couldn't update Account List Option!";
      toast.error(errorMessage);
    }
  };

  const handleToggleStatus = async (listOption) => {
    const payload = {
      ...listOption,
      isActive: !listOption?.isActive,
    };

    try {
      await updateAccountListOption(payload).unwrap();
      toast.success("Account List Option Status updated!");
      handleClose();
    } catch (err) {
      const errorMessage =
        err?.data?.message || "Couldn't update Account List Option Status!";
      toast.error(errorMessage);
    }
  };

  const handleReorderOptions = async () => {
    const reorderedList = [...orderedList]?.map(({ _id, serialNumber }) => ({
      _id,
      serialNumber,
    }));

    try {
      await reorderListItems({ reorderedList }).unwrap();
      toast.success("Account List Options successfully reordered!");
    } catch (err) {
      toast.err(err?.data?.message ?? "Couldn't reorder Account List Options!");
    }
  };

  React.useEffect(() => {
    if (listId) fetchAccountList(listId);
  }, [fetchAccountList, listId]);

  useAppMeta(
    !listTypeParams?.length ? "List Detail" : `List - ${listTypeParams.at(-1)}`
  );

  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      isBusy={loadingList || fetchingList}
      headerActions={
        <div className="flex gap-4">
          {!!orderedList?.length && (
            <LoadingButton
              loading={reOrderingList}
              loadingIndicator="Saving..."
              variant="outlined"
              color="success"
              onClick={handleReorderOptions}
            >
              Save Order
            </LoadingButton>
          )}

          {_showPopulateDefaults && (
            <PopulatePerformancePoints
              isBusy={isLoading}
              listDetail={_currentListStaticDetail}
              onPopulateOptions={handlePopulateAccountOptions}
            />
          )}

          {!_currentListStaticDetail?.readOnly && (
            <Button
              disabled={reOrderingList}
              startIcon={<Add />}
              variant="contained"
              onClick={() => setShowForm(true)}
            >
              New Option
            </Button>
          )}
        </div>
      }
    >
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        {!loadingList && !fetchingList ? (
          <AppDraggableList
            isBusy={reOrderingList}
            onDragEnd={handleOnDragEnd}
            listItems={orderedList}
            draggableItem={({ data }) => (
              <ListOptionItem
                listOption={{
                  ...data,
                  valueLabel: _currentListStaticDetail?.valueLabel,
                  valueType: _currentListStaticDetail?.valueType,
                }}
                isActive={data?.isActive}
                onViewSubOptions={(opt) =>
                  navigate(
                    `/app/crm/account-list/detail/${opt?._id}?type=${[
                      _type,
                      opt?.name,
                    ]}`
                  )
                }
                onSelectOption={(option) => {
                  setShowForm(true);
                  setSelectedOption(option);
                }}
                onToggleOptionStatus={handleToggleStatus}
              />
            )}
          />
        ) : (
          <RowsSkeleton />
        )}
      </BreadcrumbLayout.Paper>

      <FormDrawer
        title={`List Option - ${_currentListType}`}
        open={showForm}
        onClose={handleClose}
      >
        <ListOptionForm
          isBusy={addingOption || updatingOption}
          {...{
            ...(selectedOption && {
              option: {
                ...selectedOption,
                status: selectedOption?.isActive ? "Active" : "Inactive",
              },
            }),
          }}
          groupOptions={_currentListStaticDetail?.groups}
          valueLabel={_currentListStaticDetail?.valueLabel}
          valueType={_currentListStaticDetail?.valueType}
          readOnly={_currentListStaticDetail?.readOnly}
          onSubmit={(data) =>
            selectedOption ? handleUpdateOption(data) : handleAddOption(data)
          }
          onClose={handleClose}
        />
      </FormDrawer>
    </BreadcrumbLayout>
  );
};

export default AccountListOptionDetailScreen;
